import { empty, isTrue } from "helpers/common";

// Helpers for the state-employee fill form: resolving the form definition into checkbox-level
// fields and building the write payload.

// Must stay in sync with the backend's write-role list.
export const STATE_WRITE_ROLES = ['application-admin', 'hr'];

export const writableByAdminOrHr = (node) => {
  const roles = node?.can_write_roles || [];
  return roles.some(r => STATE_WRITE_ROLES.includes(r));
};

// Layout nodes nest their inputs under columns -> components.
export const flattenCompoundInputs = (components = []) => {
  const inputs = [];
  for (const node of components) {
    if (node?.type === 'input') {
      inputs.push(node);
    } else if (node?.type === 'layout') {
      for (const col of node?.columns || []) {
        inputs.push(...flattenCompoundInputs(col?.components || []));
      }
    }
  }
  return inputs;
};

// Non-writable fields are dropped; layout compounds are split so each writable input gets its own
// checkbox. Returns the checkbox fields plus a sub-identifier -> checkbox-key map for auto-ticking.
export const resolveStateFillFields = (sections) => {
  const checkboxFields = [];
  const subToParent = {};

  // `required` comes from the field; split layout inputs inherit it from their parent.
  const pushField = (field, inputs, required) => {
    const key = field.identifier;
    const descriptors = inputs.map(inp => ({
      identifier: inp.identifier,
      storedInTable: inp.stored_in_table,
      source: inp.source,
      slug: inp.slug,
      inputType: inp.input_type || inp.field_type,
    }));
    descriptors.forEach(d => { subToParent[d.identifier] = key; });
    checkboxFields.push({ field, key, inputs: descriptors, required: !!required });
  };

  for (const section of sections || []) {
    if (section?.is_active === false) continue;
    for (const field of section?.fields || []) {
      if (field?.is_active === false) continue;

      if (field?.field_type === 'compound' && (field?.components || []).some(c => c?.type === 'layout')) {
        for (const inp of flattenCompoundInputs(field?.components).filter(writableByAdminOrHr)) {
          pushField({ ...inp, field_type: inp.input_type }, [inp], field?.is_required);
        }
        continue;
      }

      let inputs;
      if (field?.field_type === 'compound') {
        inputs = flattenCompoundInputs(field?.components).filter(writableByAdminOrHr);
      } else {
        inputs = writableByAdminOrHr(field) ? [field] : [];
      }
      if (inputs.length === 0) continue;

      pushField(field, inputs, field?.is_required);
    }
  }

  return { checkboxFields, subToParent };
};

// Columns written by bare identifier. Must stay in sync with getBulkUpdateFields in the
// backend's Employee model (stored_in_table employees / employee_period_data, plus gender).
const COLUMN_TABLES = ['employees', 'employee_period_data'];

// Payload key per field: column fields by identifier, other fields as
// `employee_information.<identifier>`, and state_employee_config fields by slug.
// An untouched boolean shows "Nein", so it is sent as false, not ''.
export const buildStateDataToUpdate = (checkboxFields, checked, values) => {
  const out = [];
  for (const cf of checkboxFields) {
    if (!checked[cf.key]) continue;
    for (const inp of cf.inputs) {
      let key;
      if (inp.source === 'state_employee_config') key = inp.slug || inp.identifier;
      else if (COLUMN_TABLES.includes(inp.storedInTable) || inp.identifier === 'gender') key = inp.identifier;
      else key = `employee_information.${inp.identifier}`;
      const value = inp.inputType === 'boolean' ? isTrue(values[inp.identifier]) : (values[inp.identifier] ?? '');
      out.push({ key, value });
    }
  }
  return out;
};

// First ticked required field whose inputs are all empty, or null. A boolean always has a value.
export const findEmptyRequiredStateField = (checkboxFields, checked, values) => (
  checkboxFields.find(cf => (
    checked[cf.key]
    && cf.required
    && cf.inputs.every(inp => inp.inputType !== 'boolean' && empty(values[inp.identifier]))
  )) || null
);
